import { memo, useCallback, useRef, useState } from 'react';

import { Modal } from '@stratumn/atomic';

import type { editor } from 'monaco-editor';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';

import { CodeEditor } from '@/components/monaco/codeEditor/codeEditor';
import { notify } from '@/components/toast';
import { Tray } from '@/components/ui/tray/tray';
import { TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';

type Props = {
  inModal?: boolean;
  title?: string;
  jsonString: string;
  onSubmit?: (jsonString: string | undefined) => Promise<void>;
  onClose: () => void;
  /** Monaco editor options */
  editorOptions?: editor.IStandaloneEditorConstructionOptions;
  submitButtonLabel?: string;
  closeAfterSubmit?: boolean;
};

export const JsonEditor = memo(
  ({
    inModal = false,
    title = 'Edit JSON string',
    jsonString,
    onSubmit,
    onClose,
    editorOptions,
    submitButtonLabel = 'Save',
    closeAfterSubmit = false
  }: Props) => {
    const [parsingErr, setParsingErr] = useState<string | undefined>(undefined);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [currentValue, setCurrentValue] = useState(jsonString);

    const submit = async () => {
      if (!onSubmit) return;
      const currentStr = editorRef.current?.getValue() ?? currentValue;

      try {
        if (currentStr) JSON.parse(currentStr);
        setParsingErr(undefined);
      } catch (e) {
        const errorStr = `Error parsing object:\n${e instanceof Error ? e.toString() : 'Unknown error'}`;
        notify.error(errorStr);
        setParsingErr(errorStr);
        return;
      }

      setSubmitting(true);
      await onSubmit(currentStr);
      setSubmitting(false);
      if (closeAfterSubmit) {
        onClose();
      }
    };

    const handleEditorMount = useCallback(
      (editorInstance: editor.IStandaloneCodeEditor) => {
        editorRef.current = editorInstance;
      },
      []
    );

    const handleChange = useCallback((value: string) => {
      setCurrentValue(value);
      // Clear parsing error when user edits
      setParsingErr(undefined);
    }, []);

    const handleResize = useCallback(() => {
      editorRef.current?.layout();
    }, []);

    const editorComponent = (
      <div
        className={cn(
          'flex h-full flex-col flex-nowrap',
          inModal && 'overflow-auto'
        )}
      >
        <div
          className={cn(
            'h-full overflow-hidden text-sm/tight',
            inModal && 'overflow-auto p-3.5'
          )}
          data-cy="editor-body"
        >
          <CodeEditor
            className="h-full min-h-[300px] border-0"
            onChange={handleChange}
            onMount={handleEditorMount}
            options={editorOptions}
            readOnly={false}
            value={jsonString}
          />
        </div>
        <div className="flex h-[70px] w-full flex-row flex-nowrap items-center justify-between border-t p-4">
          {parsingErr && (
            <div
              className="truncate text-base text-destructive"
              data-testid="json-editor-error-message"
              title={parsingErr}
            >
              Invalid JSON string! {parsingErr}
            </div>
          )}
          <div className="flex w-full flex-row flex-nowrap items-center justify-end gap-4">
            <Button
              aria-label="Close the editor"
              data-cy="json-editor-close-btn"
              data-testid="json-editor-close-btn"
              disabled={false}
              onClick={onClose}
              variant="outline"
            >
              CLOSE
            </Button>
            {onSubmit && (
              <Button
                aria-label="Submit the new value"
                data-cy="json-editor-submit-btn"
                data-testid="json-editor-submit-btn"
                disabled={submitting}
                onClick={submit}
              >
                {submitButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    );

    return inModal ? (
      <Modal handleCollapse={onClose} large title={title}>
        {editorComponent}
      </Modal>
    ) : (
      <Tray
        onClose={onClose}
        onResize={handleResize}
        portalEl={document.getElementById(TRAY_PORTAL_RIGHT)}
        title={title}
        width={600}
      >
        {editorComponent}
      </Tray>
    );
  }
);
